// =============================================================================
// Evacuación de emergencia (PRL)
// =============================================================================
//
// El backend existía, montado en el router y testeado a nivel de servicio, y
// NINGUNA pantalla lo llamaba: en el frontend entero no había una sola
// petición a `/evacuation/*`, así que el día de la emergencia el listado de
// quién está dentro sólo existía para quien supiera construir la petición HTTP
// a mano (ronda 3, r2b17[5]). La ronda 4 cerró el ciclo (r2b17[3]): pasar
// lista (localizado/desaparecido por persona), recuento y cierre, además del
// arranque.
import { apiClient } from './http';

export type EmergencyType =
  | 'fire'
  | 'accident'
  | 'chemical'
  | 'earthquake'
  | 'bomb_threat'
  | 'other';

/** Espejo de la persona que devuelve el backend (PersonOnSite.to_dict). */
export interface EvacuationPerson {
  user_id: number;
  full_name: string;
  role: string;
  worksite_id: number | null;
  worksite_name: string | null;
  hours_on_site: number;
  status: 'on_site' | 'located' | 'missing' | 'off_site';
}

/** Respuesta de POST /evacuation/start: el recuento inicial con el que se pasa lista. */
export interface EvacuationStart {
  evacuation_id: string;
  emergency_type: EmergencyType;
  started_at: string;
  total_headcount: number;
  people: EvacuationPerson[];
}

/**
 * Estado de una evacuación (GET /evacuation/{id}): recuento y personas.
 *
 * Consultable también DESPUÉS del cierre (investigación, aseguradora):
 * `hours_on_site` viaja congelado a la hora de cierre y `status` pasa a
 * 'completed'.
 */
export interface EvacuationStatus {
  evacuation_id: string;
  emergency_type: EmergencyType;
  started_at: string;
  ended_at: string | null;
  total_headcount: number;
  located_count: number;
  missing_count: number;
  pending_count: number;
  status: 'active' | 'completed' | string;
  people: EvacuationPerson[];
}

export async function startEvacuation(
  worksiteId: number,
  emergencyType: EmergencyType = 'fire',
): Promise<EvacuationStart> {
  const res = await apiClient.post<EvacuationStart>('/evacuation/start', {
    emergency_type: emergencyType,
    worksite_id: worksiteId,
  });
  return res.data;
}

export async function getEvacuation(evacuationId: string): Promise<EvacuationStatus> {
  const res = await apiClient.get<EvacuationStatus>(`/evacuation/${evacuationId}`);
  return res.data;
}

export async function markPersonLocated(
  evacuationId: string,
  userId: number,
): Promise<void> {
  await apiClient.post(`/evacuation/${evacuationId}/located`, { user_id: userId });
}

export async function markPersonMissing(
  evacuationId: string,
  userId: number,
): Promise<void> {
  await apiClient.post(`/evacuation/${evacuationId}/missing`, { user_id: userId });
}

export async function endEvacuation(evacuationId: string): Promise<void> {
  await apiClient.post(`/evacuation/${evacuationId}/end`);
}
